import { put, select, takeEvery } from "redux-saga/effects";
import * as flagsActionTypes from "store/actions/input/flags/flagsActionTypes";
import * as flagActions from "store/actions/input/flags/flagsActions";
import * as pricingActionTypes from "store/actions/pricing/pricingActionTypes";
import { updateLayer } from "store/actions/pricing/pricingActions";
import * as flagSelectors from "store/selectors/input/flags/flagsSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

// Primary GL Rating flag rows (by type) ↔ the active-layer fields edited on GL Pricing
const FLAG_TYPE_TO_LAYER_FIELD = {
  OCCURRENCE_LIMIT: "glOccurrenceLimit",
  GEN_AGG_LIMIT: "glGeneralAggregate",
  PCO_AGG_LIMIT: "glPcoAggregate",
  DAMAGE_TO_PREMISES: "damageToPremisesLimit",
  MED_PAY: "medicalExpenseLimit",
  PI_AI_LIMIT: "personalAndAdvertisingInjuryLimit",
  DEDUCTIBLE: "deductibleAmount",
  LIQUOR_LIABILITY_EACH_LIMIT: "liquorOccurrenceLimit",
  LIQUOR_LIABILITY_AGG_LIMIT: "liquorAggregateLimit",
};

export const layerFieldForFlag = (flag) =>
  FLAG_TYPE_TO_LAYER_FIELD[flag?.type] ?? null;

// "$2,000,000" / "2000000" / 2000000 → 2000000; "Excluded" etc. → null
export const categoryToNumber = (category) => {
  if (category == null || category === "") {
    return null;
  }
  if (typeof category === "number") {
    return isNaN(category) ? null : category;
  }
  const parsed = parseFloat(String(category).replace(/[$,\s]/g, ""));
  return isNaN(parsed) ? null : parsed;
};

const categoryForValue = (flag, value) =>
  value == null
    ? null
    : (flag?.categoryOptions ?? []).find(
        (option) => categoryToNumber(option.key) === value
      )?.key ?? null;

// Mirror dispatches are tagged so the opposite watcher ignores the echo
const SYNC_META = { glRatingLimitsSync: true };

export function* syncFlagToLayer(action) {
  if (action.meta?.glRatingLimitsSync) {
    return;
  }
  const { id, column, value } = action.payload ?? {};
  if (column !== "category") {
    return;
  }
  const flags = yield select(flagSelectors.selectFlags);
  const flag = (flags?.exposures ?? []).find((exposure) => exposure.id === id);
  const field = layerFieldForFlag(flag);
  if (!field) {
    return;
  }
  const layer = yield select(pricingSelectors.getActiveLayer);
  if (!layer?.id) {
    return;
  }
  const numericValue = categoryToNumber(value);
  if ((layer[field] ?? null) === numericValue) {
    return;
  }
  yield put({
    ...updateLayer({ layerId: layer.id, values: { [field]: numericValue } }),
    meta: SYNC_META,
  });
}

export function* syncLayerToFlags(action) {
  if (action.meta?.glRatingLimitsSync) {
    return;
  }
  const { layerId, values } = action.payload ?? {};
  if (values == null) {
    return;
  }
  const layer = yield select(pricingSelectors.getActiveLayer);
  if (!layer?.id || layer.id !== layerId) {
    return;
  }
  const flags = yield select(flagSelectors.selectFlags);
  for (const flag of flags?.exposures ?? []) {
    const field = layerFieldForFlag(flag);
    if (!field || !(field in values)) {
      continue;
    }
    const value = values[field] ?? null;
    if (categoryToNumber(flag.category) === value) {
      continue;
    }
    yield put({
      ...flagActions.updateExposureFlag(
        flag.id,
        "category",
        categoryForValue(flag, value)
      ),
      meta: SYNC_META,
    });
  }
}

export default function* glRatingLimitsSaga() {
  yield takeEvery(flagsActionTypes.UPDATE_EXPOSURE_FLAG, syncFlagToLayer);
  yield takeEvery(pricingActionTypes.UPDATE_LAYER, syncLayerToFlags);
}
